import {
  Directive,
  DestroyRef,
  OnDestroy,
  effect,
  inject,
  input,
  output,
  untracked,
} from '@angular/core';
import { HotkeysService } from './hotkeys.service';

/**
 * Declarative hotkey binding on a host element.
 *
 * @example
 * ```html
 * <button hotkey="ctrl+s" hotkeyId="save" hotkeyDescription="Uložit" (hotkeyAction)="save()">
 *   Uložit
 * </button>
 * ```
 */
@Directive({
  selector: '[tcHotkey], [hotkey]',
  standalone: true,
  host: {
    '[attr.data-tc-hotkey]': 'tcHotkey()',
  },
})
export class HotkeyDirective implements OnDestroy {
  private readonly hotkeys = inject(HotkeysService);
  private readonly destroyRef = inject(DestroyRef);
  private registeredId: string | null = null;

  /** Key combination (alias: `hotkey`). */
  readonly tcHotkey = input.required<string>({ alias: 'hotkey' });
  /** Unique id — defaults to combo string. */
  readonly hotkeyId = input<string>('');
  readonly hotkeyDescription = input<string>('');
  readonly hotkeyScope = input<string | string[]>('global');
  readonly hotkeyPriority = input<number>(0);
  readonly hotkeyAllowInInputs = input<boolean>(false);
  readonly hotkeyEnabled = input<boolean>(true);

  /** Emitted when the combo is pressed (also runs `hotkeyAction` if bound). */
  readonly hotkeyAction = output<void>();

  constructor() {
    effect(() => {
      this.tcHotkey();
      this.hotkeyId();
      this.hotkeyDescription();
      this.hotkeyScope();
      this.hotkeyPriority();
      this.hotkeyAllowInInputs();
      this.hotkeyEnabled();

      untracked(() => this.syncRegistration());
    });

    this.destroyRef.onDestroy(() => this.unregister());
  }

  ngOnDestroy(): void {
    this.unregister();
  }

  private syncRegistration(): void {
    if (!this.hotkeyEnabled()) {
      this.unregister();
      return;
    }

    const combo = this.tcHotkey();
    if (!combo) {
      this.unregister();
      return;
    }

    const id = this.hotkeyId() || `hotkey:${combo}`;
    if (this.registeredId && this.registeredId !== id) {
      this.hotkeys.unregister(this.registeredId);
      this.registeredId = null;
    }

    this.hotkeys.register({
      id,
      combo,
      description: this.hotkeyDescription() || undefined,
      scope: this.hotkeyScope(),
      priority: this.hotkeyPriority(),
      allowInInputs: this.hotkeyAllowInInputs(),
      handler: () => {
        this.hotkeyAction.emit();
      },
    });
    this.registeredId = id;
  }

  private unregister(): void {
    if (!this.registeredId) {
      return;
    }
    this.hotkeys.unregister(this.registeredId);
    this.registeredId = null;
  }
}
